import { FieldState, ObjectConfig, required } from "@homebound/form-state";
import {
  IncrementalCollectionOp,
  InputMaybe,
  Maybe,
  SaveMaterialLeadTimeConfigVariantIncrementalInput,
  SaveOptionPricingConfigVariantIncrementalInput,
} from "src/generated/graphqlTypes";
import { BoundMaterialLeadTimeConfigFields_MaterialLeadTimeConfigFragment } from "src/routes/admin/components/BoundMaterialLeadTimeConfigFields.generated";
export type OptionPricingFormValue = {
  id?: InputMaybe<string>;
  parentId?: InputMaybe<string>;
  postCutoffDesiredMarginInBasisPoints?: InputMaybe<number>;
  preCutoffDesiredMarginInBasisPoints?: InputMaybe<number>;
  selectionFeeInCents?: InputMaybe<number>;
  minimumPriceInCents?: InputMaybe<number>;
  reasonNote?: InputMaybe<string>;
};

export type MaterialLeadTimeFormValue = {
  id?: InputMaybe<string>;
  parentId?: InputMaybe<string>;
  minLeadTimeInDays?: InputMaybe<number>;
  expectedLeadTimeInDays?: InputMaybe<number>;
  maxLeadTimeInDays?: InputMaybe<number>;
  reasonNote?: InputMaybe<string>;
  variants?: Maybe<MaterialLeadTimeConfigVariantInput[]>;
};

export function maybeOptionPricingConfig(
  optionPricingConfig?: OptionPricingFormValue,
  variants?: Maybe<SaveOptionPricingConfigVariantIncrementalInput[]>,
) {
  if (!optionPricingConfig) return;

  const {
    postCutoffDesiredMarginInBasisPoints,
    preCutoffDesiredMarginInBasisPoints,
    selectionFeeInCents,
    minimumPriceInCents,
    reasonNote,
    id: maybeId,
  } = optionPricingConfig;

  return {
    optionPricingConfig: {
      id: maybeId,
      postCutoffDesiredMarginInBasisPoints,
      preCutoffDesiredMarginInBasisPoints,
      selectionFeeInCents,
      minimumPriceInCents,
      reasonNote,
      ...(variants?.nonEmpty ? { variants } : {}),
    },
  };
}

export function maybeMaterialLeadTimeConfig(materialLeadTimeConfig?: MaterialLeadTimeFormValue) {
  if (!materialLeadTimeConfig) return;

  const {
    minLeadTimeInDays,
    expectedLeadTimeInDays,
    maxLeadTimeInDays,
    id: maybeId,
    reasonNote,
    variants,
  } = materialLeadTimeConfig;

  return {
    materialLeadTimeConfig: {
      id: maybeId,
      minLeadTimeInDays,
      expectedLeadTimeInDays,
      maxLeadTimeInDays,
      reasonNote,
      variants: variants?.map(({ marketName, ...others }) => ({ ...others })) ?? [],
    },
  };
}

// helper function to initialize the form state for MaterialLeadTimeConfig used in CostCodes/ItemCodes and Material Listing
export function initializeMaterialLeadTimeConfigFields(
  materialLeadTimeConfig: BoundMaterialLeadTimeConfigFields_MaterialLeadTimeConfigFragment | null | undefined,
): MaterialLeadTimeFormValue {
  const { variants, ...others } = materialLeadTimeConfig ?? {};
  return {
    variants:
      materialLeadTimeConfig?.variants?.map(({ id, market, ...others }) => ({
        ...others,
        id,
        marketId: market?.id,
        marketName: market?.name,
        op: IncrementalCollectionOp.Include,
      })) ?? [],
    ...others,
  };
}

export type MaterialLeadTimeConfigVariantInput = SaveMaterialLeadTimeConfigVariantIncrementalInput & {
  marketName?: InputMaybe<string>;
};

// Standardize the form config(s) for the Material Lead Time Config and its variants
const materialLeadTimeConfigVariantsConfig: ObjectConfig<MaterialLeadTimeConfigVariantInput> = {
  id: { type: "value" },
  op: { type: "value", rules: [required] },
  minLeadTimeInDays: { type: "value", rules: [required] },
  expectedLeadTimeInDays: { type: "value", rules: [required] },
  maxLeadTimeInDays: { type: "value", rules: [required] },
  marketId: { type: "value", rules: [required] },
  reasonNote: { type: "value" },
  marketName: { type: "value" },
};

export const materialLeadTimeConfigConfig: ObjectConfig<MaterialLeadTimeFormValue> = {
  id: { type: "value" },
  minLeadTimeInDays: { type: "value" },
  expectedLeadTimeInDays: { type: "value" },
  maxLeadTimeInDays: { type: "value" },
  reasonNote: { type: "value" },
  variants: { type: "list", config: materialLeadTimeConfigVariantsConfig },
};

// Standardize the form config(s) for the Option PricingConfig and its variants
export const optionPricingConfigVariantsConfig: ObjectConfig<SaveOptionPricingConfigVariantIncrementalInput> = {
  op: { type: "value", rules: [required] },
  id: { type: "value" },
  projectIds: { type: "value" },
  preCutoffDesiredMarginInBasisPoints: { type: "value" },
  postCutoffDesiredMarginInBasisPoints: { type: "value" },
  selectionFeeInCents: { type: "value" },
  minimumPriceInCents: { type: "value" },
  reasonNote: { type: "value" },
};

export const optionPricingConfigConfig: ObjectConfig<OptionPricingFormValue> = {
  id: { type: "value" },
  preCutoffDesiredMarginInBasisPoints: { type: "value" },
  postCutoffDesiredMarginInBasisPoints: { type: "value" },
  selectionFeeInCents: { type: "value" },
  minimumPriceInCents: { type: "value" },
  reasonNote: { type: "value" },
};

// helper function to set the validation rules for the reasonNote field on OptionPricingConfigs and MaterialLeadTimeConfigs
export function handleReasonNoteValidationRules(
  isDirty: boolean,
  reasonNote: FieldState<InputMaybe<string>>,
  originalReasonNoteValue: InputMaybe<string>,
  hasExistingValues: boolean,
) {
  if (isDirty && hasExistingValues) {
    reasonNote.set("");
    // Only make required if there was an original value (i.e., editing, not creating)
    if (!reasonNote.rules.includes(required)) {
      reasonNote.rules.push(required);
    }
  } else {
    // If we revert the changes, then removed the required rule and set reasonNote back to its original value
    reasonNote.rules.remove?.(required);
    reasonNote.set(originalReasonNoteValue);
  }
}
